import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import { formatDistanceToNow } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faArchive, faTrash, faSearch, faPlus, faFilter, faEdit, faCheck, faTimes, faComments } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { 
  filterConversationsByTab, 
  searchConversations, 
  sortConversationsByDate,
  filterConversationsByDateRange 
} from '../utils/chatSearch';
import { getExperienceType } from '../data/experienceTypes';
import ChatView from '../components/chat/ChatView';
import { PageHeader, Button } from '../design-system';

export default function Chats() {
  const [conversations, setConversations] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'favorites', 'archived'
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const { user } = useAuth();
  const { familyId } = useFamily();
  const navigate = useNavigate();

  // Load conversations
  useEffect(() => {
    if (!familyId) return;

    const q = query(
      collection(db, 'conversations'),
      where('familyId', '==', familyId),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by updatedAt in case index isn't ready yet
      convs.sort((a, b) => {
        const aTime = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : (a.updatedAt ? new Date(a.updatedAt).getTime() : 0);
        const bTime = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : (b.updatedAt ? new Date(b.updatedAt).getTime() : 0);
        return bTime - aTime; // Descending order
      });
      setConversations(convs);
    }, (error) => {
      console.error('Error loading conversations:', error);
      // If index error, try without orderBy as fallback
      if (error.code === 'failed-precondition' && error.message?.includes('index')) {
        console.log('Index not ready yet, loading without orderBy...');
        const fallbackQuery = query(
          collection(db, 'conversations'),
          where('familyId', '==', familyId)
        );
        const fallbackUnsubscribe = onSnapshot(fallbackQuery, (snapshot) => {
          const convs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Sort by updatedAt in memory
          convs.sort((a, b) => {
            const aTime = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : (a.updatedAt ? new Date(a.updatedAt).getTime() : 0);
            const bTime = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : (b.updatedAt ? new Date(b.updatedAt).getTime() : 0);
            return bTime - aTime; // Descending order
          });
          setConversations(convs);
        }, (fallbackError) => {
          console.error('Error loading conversations (fallback):', fallbackError);
          toast.error('Failed to load conversations');
        });
        return fallbackUnsubscribe;
      } else {
        toast.error('Failed to load conversations');
      }
    });

    return unsubscribe;
  }, [familyId]);

  // Filter and sort conversations
  const filteredConversations = sortConversationsByDate(
    filterConversationsByDateRange(
      searchConversations(
        filterConversationsByTab(conversations, activeTab),
        searchQuery
      ),
      dateFilter
    )
  );

  const handleToggleFavorite = async (conversationId, currentFavorite) => {
    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        favorite: !currentFavorite,
        updatedAt: Timestamp.now(),
      });
      toast.success(currentFavorite ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorite');
    }
  };

  const handleArchive = async (conversationId, currentArchived) => {
    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        archived: !currentArchived,
        updatedAt: Timestamp.now(),
      });
      toast.success(currentArchived ? 'Unarchived' : 'Archived');
    } catch (error) {
      console.error('Error archiving conversation:', error);
      toast.error('Failed to archive conversation');
    }
  };

  const handleDelete = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'conversations', conversationId));
      toast.success('Conversation deleted');
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const handleStartEdit = (conversation) => {
    setEditingConversationId(conversation.id);
    setEditTitle(conversation.title || '');
  };

  const handleCancelEdit = () => {
    setEditingConversationId(null);
    setEditTitle('');
  };

  const handleSaveEdit = async (conversationId) => {
    const trimmedTitle = editTitle.trim();
    if (!trimmedTitle) {
      toast.error('Title cannot be empty');
      return;
    }

    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        title: trimmedTitle,
        updatedAt: Timestamp.now(),
      });
      toast.success('Conversation renamed');
      setEditingConversationId(null);
      setEditTitle('');
      
      // Update selected conversation if it's the one being edited
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation({
          ...selectedConversation,
          title: trimmedTitle
        });
      }
    } catch (error) {
      console.error('Error renaming conversation:', error);
      toast.error('Failed to rename conversation');
    }
  };

  const getFirstMessage = (conversation) => {
    if (!conversation.messages || conversation.messages.length === 0) return 'No messages';
    const firstUserMessage = conversation.messages.find(msg => msg.role === 'user');
    return firstUserMessage?.content?.substring(0, 100) || 'No messages';
  };

  const getExperienceBadge = (conversation) => {
    if (!conversation.projectId) return null;
    const experienceType = getExperienceType(conversation.projectType);
    return experienceType?.name || 'Experience';
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-6">
      <PageHeader
        title="Chat History"
        subtitle="View and manage your conversations"
        actions={
          <Button
            onClick={() => {
              // Dispatch custom event to open chat widget
              window.dispatchEvent(new CustomEvent('openChat'));
              navigate('/');
            }}
            className="flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>New Chat</span>
          </Button>
        }
        maxWidth="1280px"
      >
        {/* Tabs - Full width border on desktop */}
        <div className="overflow-x-auto no-scrollbar border-t border-b border-border -mx-4 px-4 md:-mx-10 md:px-10">
          <div className="flex gap-2 sm:gap-3 min-w-max md:min-w-0">
            {[
              { id: 'all', label: 'All', icon: faComments, count: filterConversationsByTab(conversations, 'all').length },
              { id: 'favorites', label: 'Favorites', icon: faStar, count: filterConversationsByTab(conversations, 'favorites').length },
              { id: 'archived', label: 'Archived', icon: faArchive, count: filterConversationsByTab(conversations, 'archived').length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 sm:px-6 py-2 text-sm sm:text-base font-semibold border-b-2 transition-colors touch-target whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary active:bg-gray-50'
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id
                      ? 'bg-primary/20 text-primary'
                      : 'bg-surface text-text-secondary'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </PageHeader>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
          <div className="flex-1 relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-base border border-border rounded-lg focus:outline-none focus:border-primary transition-all"
            />
          </div>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-4 py-2.5 text-base border border-border rounded-lg focus:outline-none focus:border-primary transition-all bg-white"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>

        {/* Conversations List */}
        {filteredConversations.length === 0 ? (
          <div className="bg-white rounded-card shadow-card p-8 sm:p-12 text-center">
            <div className="mb-4">
              <FontAwesomeIcon icon={faSearch} className="text-4xl text-text-secondary" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">
              {activeTab === 'favorites' 
                ? 'No favorite conversations yet'
                : activeTab === 'archived'
                ? 'No archived conversations'
                : searchQuery
                ? 'No conversations found'
                : 'No conversations yet'}
            </h3>
            <p className="text-sm sm:text-base text-text-secondary mb-6">
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'Start a new conversation to get help with your Jewish family planning.'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('openChat'));
                  navigate('/');
                }}
                className="bg-primary text-white font-semibold py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
              >
                Start New Chat
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Conversation List */}
            <div className="lg:col-span-1 space-y-3">
              {filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => setSelectedConversation(conversation)}
                  className={`bg-white rounded-card p-4 sm:p-5 border border-border shadow-card hover:shadow-card-hover transition-all cursor-pointer ${
                    selectedConversation?.id === conversation.id ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    {editingConversationId === conversation.id ? (
                      <div className="flex-1 flex items-center gap-2 mr-2">
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveEdit(conversation.id);
                            } else if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 px-2 py-1 text-sm sm:text-base font-semibold text-text-primary border border-primary rounded focus:outline-none focus:ring-2 focus:ring-primary"
                          autoFocus
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveEdit(conversation.id);
                          }}
                          className="text-green-600 hover:text-green-700 transition-colors p-1"
                          title="Save"
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelEdit();
                          }}
                          className="text-red-600 hover:text-red-700 transition-colors p-1"
                          title="Cancel"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <h3 
                          className="font-semibold text-text-primary text-sm sm:text-base flex-1 line-clamp-1 cursor-pointer hover:text-primary transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartEdit(conversation);
                          }}
                          title="Click to rename"
                        >
                          {conversation.title || 'New Conversation'}
                        </h3>
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEdit(conversation);
                            }}
                            className="text-text-secondary hover:text-primary transition-colors p-1"
                            title="Rename"
                          >
                            <FontAwesomeIcon icon={faEdit} className="text-xs" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleFavorite(conversation.id, conversation.favorite);
                            }}
                            className={`${
                              conversation.favorite ? 'text-yellow-500' : 'text-text-secondary hover:text-yellow-500'
                            } transition-colors p-1`}
                            title={conversation.favorite ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            <FontAwesomeIcon icon={faStar} className={conversation.favorite ? 'fas' : 'far'} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  
                  <p className="text-xs sm:text-sm text-text-secondary line-clamp-2 mb-2">
                    {getFirstMessage(conversation)}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getExperienceBadge(conversation) && (
                        <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full">
                          {getExperienceBadge(conversation)}
                        </span>
                      )}
                      <span className="text-xs text-text-secondary">
                        {conversation.updatedAt
                          ? formatDistanceToNow(
                              conversation.updatedAt?.toDate 
                                ? conversation.updatedAt.toDate() 
                                : new Date(conversation.updatedAt),
                              { addSuffix: true }
                            )
                          : 'Just now'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchive(conversation.id, conversation.archived);
                        }}
                        className="text-text-secondary hover:text-text-primary transition-colors p-1"
                        title={conversation.archived ? 'Unarchive' : 'Archive'}
                      >
                        <FontAwesomeIcon icon={faArchive} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(conversation.id);
                        }}
                        className="text-text-secondary hover:text-red-600 transition-colors p-1"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Chat View */}
            <div className="lg:col-span-2">
              {selectedConversation ? (
                <ChatView 
                  conversation={selectedConversation} 
                  onClose={() => setSelectedConversation(null)}
                />
              ) : (
                <div className="bg-white rounded-card shadow-card p-8 sm:p-12 text-center h-full flex items-center justify-center">
                  <div>
                    <p className="text-text-secondary">Select a conversation to view</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

